// Pure measure/geometry math for LRS (Linear Referencing System) routes.
// A "section" is a NormalizedSection: { paths: [ [ [lng, lat, m], ... ], ... ], ... }.
// Measures (m) are miles. No network, no React — keep this file dependency-free.

const RANGE_EPSILON = 0.005;

function pathMeasureRange(path) {
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of path) {
    const m = v[2];
    if (typeof m !== 'number' || Number.isNaN(m)) continue;
    if (m < lo) lo = m;
    if (m > hi) hi = m;
  }
  return lo <= hi ? [lo, hi] : null;
}

export function measureRangesFromSections(sections) {
  const raw = [];
  for (const s of sections ?? []) {
    for (const path of s?.paths ?? []) {
      const r = pathMeasureRange(path);
      if (r) raw.push(r);
    }
  }
  raw.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [lo, hi] of raw) {
    const last = merged[merged.length - 1];
    if (last && lo <= last[1] + RANGE_EPSILON) {
      last[1] = Math.max(last[1], hi);
    } else {
      merged.push([lo, hi]);
    }
  }
  return merged;
}

export function isMeasureInRanges(ranges, m, epsilon = RANGE_EPSILON) {
  if (!Number.isFinite(m)) return false;
  return (ranges ?? []).some(([lo, hi]) => m >= lo - epsilon && m <= hi + epsilon);
}

export function interpolatePointAtMeasure(sections, m) {
  if (!Number.isFinite(m)) return null;
  for (const s of sections ?? []) {
    for (const path of s?.paths ?? []) {
      for (let i = 0; i < path.length - 1; i += 1) {
        const [lng1, lat1, m1] = path[i];
        const [lng2, lat2, m2] = path[i + 1];
        if (!Number.isFinite(m1) || !Number.isFinite(m2) || m1 === m2) continue;
        const lo = Math.min(m1, m2);
        const hi = Math.max(m1, m2);
        if (m < lo || m > hi) continue;
        const t = (m - m1) / (m2 - m1);
        return { lat: lat1 + t * (lat2 - lat1), lng: lng1 + t * (lng2 - lng1) };
      }
    }
  }
  return null;
}

// Local equirectangular approximation (fine at sub-km scale).
function toLocalMeters(refLat) {
  const kx = 111320 * Math.cos((refLat * Math.PI) / 180);
  const ky = 110540;
  return (lng, lat, refLng) => [(lng - refLng) * kx, (lat - refLat) * ky];
}

export function projectPointToMeasure(sections, point) {
  if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return null;
  const project = toLocalMeters(point.lat);
  let best = null;
  for (const s of sections ?? []) {
    for (const path of s?.paths ?? []) {
      for (let i = 0; i < path.length - 1; i += 1) {
        const [lng1, lat1, m1] = path[i];
        const [lng2, lat2, m2] = path[i + 1];
        if (!Number.isFinite(m1) || !Number.isFinite(m2)) continue;
        const [x1, y1] = project(lng1, lat1, point.lng);
        const [x2, y2] = project(lng2, lat2, point.lng);
        const dx = x2 - x1;
        const dy = y2 - y1;
        const segLen2 = dx * dx + dy * dy;
        const t = segLen2 === 0 ? 0 : Math.max(0, Math.min(1, (-x1 * dx - y1 * dy) / segLen2));
        const cx = x1 + t * dx;
        const cy = y1 + t * dy;
        const dist = Math.hypot(cx, cy);
        if (!best || dist < best.distanceMeters) {
          best = { m: m1 + t * (m2 - m1), distanceMeters: dist };
        }
      }
    }
  }
  return best;
}

export function cardinalFromSections(sections) {
  // Direction of travel = direction of increasing measure. Find the vertices
  // holding the global min and max measure and take the bearing between them.
  let minV = null;
  let maxV = null;
  for (const s of sections ?? []) {
    for (const path of s?.paths ?? []) {
      for (const v of path) {
        if (!Number.isFinite(v[2])) continue;
        if (!minV || v[2] < minV[2]) minV = v;
        if (!maxV || v[2] > maxV[2]) maxV = v;
      }
    }
  }
  if (!minV || !maxV || minV[2] === maxV[2]) return null;
  const dLat = maxV[1] - minV[1];
  const dLng = (maxV[0] - minV[0]) * Math.cos((minV[1] * Math.PI) / 180);
  if (Math.abs(dLat) >= Math.abs(dLng)) return dLat >= 0 ? 'NB' : 'SB';
  return dLng >= 0 ? 'EB' : 'WB';
}

export function lengthMismatch(expectedMiles, actualMiles) {
  const delta = Math.abs(actualMiles - expectedMiles);
  // A milepost span is a CALIBRATED measure, not a physical distance: markers
  // aren't repositioned when a road is realigned, so span vs the actual driven
  // route length legitimately differs by several percent even when nothing is
  // wrong. Google's route also follows its own (slightly different) road
  // geometry. So this check only flags GROSS mismatches that indicate the
  // router genuinely left the intended road — ~0.25 mi + 15%, not 0.1 + 5%.
  const tolerance = 0.25 + 0.15 * Math.abs(expectedMiles);
  return { mismatch: delta > tolerance, deltaMiles: delta };
}

export function formatRanges(ranges) {
  return (ranges ?? [])
    .map(([lo, hi]) => `${lo.toFixed(1)}–${hi.toFixed(1)}`)
    .join(', ');
}

/** One-line display for an LRS point location (click annotation, tooltips).
 *  "I-65 NB · MP 257.96 · East Central Region" — region only when known. */
export function formatLrsLocation(loc) {
  if (!loc || !Number.isFinite(loc.measure) || !loc.routeLabel) return null;
  const abbrev = loc.network === 'milepoint' ? 'MPT' : 'MP';
  const region = loc.zone?.region ? ` · ${loc.zone.region} Region` : '';
  return `${loc.routeLabel} · ${abbrev} ${loc.measure.toFixed(2)}${region}`;
}

/** Expected route length in miles from an lrs binding, or null when none
 *  exists (cross-road v2 routes have no comparable span). Accepts both the
 *  v1 flat shape ({mpStart, mpEnd}) and v2 ({v:2, start, end, sameRoad}). */
export function expectedSpanFromLrs(lrs) {
  if (!lrs) return null;
  if (lrs.v === 2) {
    if (!lrs.sameRoad) return null;
    const s = lrs.start?.measure;
    const e = lrs.end?.measure;
    return Number.isFinite(s) && Number.isFinite(e) ? Math.abs(e - s) : null;
  }
  const s = lrs.mpStart;
  const e = lrs.mpEnd;
  return Number.isFinite(s) && Number.isFinite(e) ? Math.abs(e - s) : null;
}
